import _ from 'lodash';

function addDepth(node, depth = 0) {
  return {
    ...node,
    children: node.children.map((child) => addDepth(child, depth + 1)),
    depth
  };
}

function getNodeByID(node, id) {
  if (node.id === id) {
    return node;
  }
  return node.children.map((child) => getNodeByID(child, id)).find((child) => child !== undefined);
}

function hasStates(metric) {
  if (metric === undefined) {
    return true;
  }
  const metricHasStates =
    metric.availablefilters && _.some(metric.availablefilters, (filter) => filter.attribute === 12);
  const childrenHasStates = _.some(metric.children, hasStates);
  return metricHasStates || childrenHasStates;
}

function hasAdjustment(metric, selectedAdjustment) {
  if (selectedAdjustment === '' || metric === undefined) {
    return true;
  }
  const hasSelectedAdjustment = (m) => hasAdjustment(m, selectedAdjustment);
  const metricHasAdjustment =
    metric.availableadjustments &&
    _.some(
      metric.availableadjustments,
      (adjustment) => adjustment.shortlabel === selectedAdjustment
    );
  const childrenHasAdjustment = _.some(metric.children, hasSelectedAdjustment);
  return metricHasAdjustment || childrenHasAdjustment;
}

function generateTreeDepthString(depth) {
  if (depth === 0) {
    return '';
  }
  const children = generateTreeDepthString(depth - 1);
  return `children {
                id
                name
                data {
                    value
                }
                availableadjustments {
                    id
                    name
                    shortlabel
                }
                availablefilters {
                    attribute
                    description
                }
                ${children}
            }`;
}

function callback(functions, state) {
  functions.forEach((f) => f(state));
}

function flattenTree(tree) {
  return _.concat([tree], _.flatten(tree.children.map(flattenTree)));
}

function getMetricsWarnings(tree, chartType, adjustment, states) {
  const flatMetrics = flattenTree(tree);
  const noState = flatMetrics.filter((metric) => !hasStates(metric));
  const noAdjustment = flatMetrics.filter((metric) => !hasAdjustment(metric, adjustment));
  let warnings = [];

  if (chartType === 1) {
    const warning = 'Metric cannot be selected because there is no state data for map.';
    warnings = _.concat(
      warnings,
      noState.map(({ id }) => ({ id, warning }))
    );
  }

  if (states.length > 1 || states[0] !== '00') {
    const warning = 'Metric cannot be selected because there is no state data.';
    warnings = _.concat(
      warnings,
      noState.map(({ id }) => ({ id, warning }))
    );
  }

  if (adjustment !== '') {
    const warning = 'Metric cannot be selected because it does not have the selected adjustment.';
    warnings = _.concat(
      warnings,
      noAdjustment.map(({ id }) => ({ id, warning }))
    );
  }

  return _(warnings)
    .groupBy('id')
    .mapValues((warns) => warns.map(({ warning }) => warning))
    .map((value, key) => ({ id: key, warnings: value }))
    .value();
}

function getChartTypeWarnings(metricTree, metrics) {
  const noState = metrics
    .map((metric) => getNodeByID(metricTree, metric))
    .filter((metric) => !hasStates(metric));
  return [
    {
      chartType: 1,
      warnings: noState.map(
        ({ name }) => `"${name.trim()}" is selected but doesn't have state data.`
      )
    }
  ];
}

function stripNullChildren(tree) {
  let children = [];
  if (Array.isArray(tree.children)) {
    children = _(tree.children)
      .filter((child) => child !== null)
      .map(stripNullChildren)
      .value();
  }
  return {
    ...tree,
    children
  };
}

function metricsHaveStates(tree, metrics) {
  const nodes = metrics.map((metric) => getNodeByID(tree, metric));
  return _.every(nodes, hasStates);
}

function getAvailableCharts(allowMap) {
  const defaultChartTypes = [0, 2, 3, 4];
  if (allowMap) {
    return _.concat(defaultChartTypes, 1);
  }
  return defaultChartTypes;
}

function getAvailableAdjustments(tree, metrics) {
  const adjustments = _(metrics)
    .filter((d) => d !== undefined)
    .map((metric) => getNodeByID(tree, metric))
    .map('availableadjustments')
    .value();

  return _.intersectionBy(...adjustments, 'name');
  // return tree.availableadjustments;
}

function getValidCompareMode({ chartType, states, metrics, compareMode }) {
  if (chartType === 4) {
    return 'all';
  }
  if (chartType === 1) {
    return 'states';
  }
  if (compareMode === 'all') {
    if (states.length > 1 && metrics.length === 1) {
      return 'states';
    }
    return 'metrics';
  }
  return compareMode;
}

function getValidStatesAndMetrics(
  { states: modelStates, metrics: modelMetrics, compareMode, metricTree },
  rootID
) {
  let metrics = modelMetrics;
  let states = modelStates;
  const defaultMetric = rootID;
  const defaultState = '00';

  if (metrics.length === 0 || _.every(metrics, (metric) => metric === undefined)) {
    metrics = [defaultMetric];
  }

  if (states.length === 0) {
    states = [defaultState];
  }

  if (compareMode === 'metrics' && states.length > 1) {
    return {
      metrics,
      states: [defaultState]
    };
  }
  if (compareMode === 'states' && metrics.length > 1) {
    const leastDepthWithStates = _(metrics)
      .map((metric) => getNodeByID(metricTree, metric))
      .filter(hasStates)
      .minBy('depth');
    if (leastDepthWithStates === undefined) {
      return {
        states,
        metrics: [defaultMetric]
      };
    }
    return {
      states,
      metrics: [leastDepthWithStates.id]
    };
  }
  return {
    states,
    metrics
  };
}

function getMetricsOptions(metrics, states, chartType, highlightState) {
  const stateFips = states.filter((state) => state !== '00');
  const usFips = states.filter((state) => state === '00');
  const usOnly = stateFips.length === 0;
  if (chartType === 4 && !usOnly) {
    const usMetrics = usFips.map(() =>
      _(metrics)
        .filter((metric) => metric !== undefined)
        .map((metric) => ({
          id: metric,
          title: 'USA'
        }))
        .value()
    );
    const statesMetrics = stateFips.map((state) =>
      _(metrics)
        .filter((metric) => metric !== undefined)
        .map((metric) => ({
          id: metric,
          states: [{ fips: state }]
        }))
        .value()
    );
    return _.concat(usMetrics, statesMetrics);
  }
  // If map
  if (chartType === 1) {
    const metric = {
      id: metrics[0]
    };
    if (usOnly) {
      metric.allStates = true;
    } else {
      metric.states = stateFips.map((fips) => ({ fips }));
    }
    return [metric];
  }

  const usMetrics = _(usFips)
    .map(() => metrics.map((id, i) => ({ id, colorIndex: i })))
    .flatten()
    .filter(({ id }) => id !== undefined)
    .value();
  const statesMetrics = _(metrics)
    .map((id) => ({
      id,
      states: stateFips.map((fips) => {
        const highlight = highlightState === fips && (chartType === 2 || chartType || 3);
        return {
          fips,
          colorIndex: parseFloat(fips),
          primaryColor: highlight ? '#015193' : undefined,
          fontSize: highlight ? 16 : undefined
        };
      })
    }))
    .filter(({ states: metricStates, id }) => metricStates.length > 0 && id !== undefined)
    .value();
  return _.concat(usMetrics, statesMetrics);
}

function addMetrics(current, newMetrics) {
  const metrics = _.clone(current);
  newMetrics.forEach((id) => {
    const missingOrDuplicateIndex = _.findIndex(metrics, (d) => d === undefined || d === id);
    if (missingOrDuplicateIndex > -1) {
      metrics[missingOrDuplicateIndex] = id;
    } else {
      metrics.push(id);
    }
  });
  return metrics;
}

function removeMetrics(current, removedMetrics) {
  const metrics = _.clone(current);
  removedMetrics.forEach((id) => {
    const currentIndex = _.findIndex(metrics, (d) => d === id);
    if (currentIndex > -1) {
      metrics[currentIndex] = undefined;
    }
  });
  return metrics;
}

export default class ChartExplorerModel {
  constructor({
    chartType = 0,
    metrics = [],
    adjustment = '',
    states = ['00'],
    compareMode = 'metrics'
  } = {}) {
    this.chartType = chartType;
    this.metrics = metrics;
    this.adjustment = adjustment;
    this.states = states;
    this.compareMode = compareMode;
    this.metricTree = null;
    this.highlightState = null;
    this.callbackFunctions = [];
  }

  getChartOptions() {
    const { chartType, metrics, adjustment, states, metricTree, highlightState } = this;

    if (metricTree === null) {
      return null;
    }

    return {
      chartType,
      adjustment: adjustment.replace(' ', ''),
      parentID: metricTree.id,
      metrics: getMetricsOptions(metrics, states, chartType, highlightState),
      defaultToOverlapYears: true,
      transition: true,
      transitionDuration: 500,
      uncollapsible: true
    };
  }

  getModelState() {
    const { chartType, adjustment, metricTree } = this;

    let { metrics, states, compareMode } = this;

    if (metricTree === null) {
      return null;
    }
    compareMode = getValidCompareMode({
      chartType,
      states,
      metrics,
      compareMode
    });
    ({ metrics, states } = getValidStatesAndMetrics(
      { states, metrics, compareMode, metricTree },
      metricTree.id
    ));

    this.compareMode = compareMode;
    this.metrics = metrics;
    this.states = states;

    const chartOptions = this.getChartOptions();
    const warnings = {
      metrics: getMetricsWarnings(metricTree, chartType, adjustment, states),
      chartType: getChartTypeWarnings(metricTree, metrics)
    };
    const selectedMetricsHaveStates = metricsHaveStates(metricTree, metrics);
    return {
      chartOptions,
      chartType,
      metrics,
      adjustment,
      states,
      compareMode,
      metricTree,
      warnings,
      hasStates: selectedMetricsHaveStates,
      availableAdjustments: getAvailableAdjustments(metricTree, metrics),
      availableChartTypes: getAvailableCharts(selectedMetricsHaveStates)
    };
  }

  registerCallback(func) {
    this.callbackFunctions.push(func);
    return this.getModelState();
  }

  setAdjustment(adjustment) {
    const fixedAdjustment = [null, 'null'].indexOf(adjustment) >= 0 ? '' : adjustment;
    this.adjustment = fixedAdjustment;
    callback(this.callbackFunctions, this.getModelState());
    return this.getModelState();
  }

  setChartType(chartType) {
    this.chartType = chartType;
    callback(this.callbackFunctions, this.getModelState());
    return this.getModelState();
  }

  setCompareMode(mode) {
    this.compareMode = mode;
    callback(this.callbackFunctions, this.getModelState());
    return this.getModelState();
  }

  toggleChildrenMetrics(metric) {
    if (this.compareMode === 'states') {
      return this.getModelState();
    }
    const node = getNodeByID(this.metricTree, metric);
    const hasCurrentAdjustment = (m) => hasAdjustment(m, this.adjustment);
    const validChildren = _(node.children)
      .filter((child) => child.data.length > 0)
      .filter(hasCurrentAdjustment)
      .value();
    const isAllOn = _.every(validChildren, (d) => _.includes(this.metrics, d.id));
    const childrenIDs = _.map(validChildren, 'id');

    if (isAllOn) {
      this.metrics = removeMetrics(this.metrics, childrenIDs);
    } else {
      this.metrics = addMetrics(this.metrics, childrenIDs);
    }

    callback(this.callbackFunctions, this.getModelState());
    return this.getModelState();
  }

  toggleMetric(metric) {
    if (this.compareMode === 'metrics' || this.compareMode === 'all') {
      if (_.includes(this.metrics, metric)) {
        this.metrics = removeMetrics(this.metrics, [metric]);
      } else {
        this.metrics = addMetrics(this.metrics, [metric]);
      }
    } else {
      this.metrics = [metric];
    }

    callback(this.callbackFunctions, this.getModelState());
    return this.getModelState();
  }

  toggleState(state) {
    if (this.compareMode === 'states' || this.compareMode === 'all') {
      if (_.includes(this.states, state)) {
        this.states = _.without(this.states, state);
      } else {
        this.states = _.concat(this.states, state);
      }
    } else {
      this.states = [state];
    }

    callback(this.callbackFunctions, this.getModelState());
    return this.getModelState();
  }

  resetAll() {
    this.chartType = 0;
    this.metrics = [];
    this.adjustment = '';
    this.states = ['00'];
    this.compareMode = 'metrics';
    callback(this.callbackFunctions, this.getModelState());
    return this.getModelState();
  }

  async setRoot(metricID, url = 'https://a.usafacts.org/graphql/') {
    const children = generateTreeDepthString(12);
    const query = `{
            metric(id: ${metricID}) {
                id
                name
                metadata {
                    id
                    data
                    type
                }
                sources {
                    agencyname
                    lastupdated
                    tableorfilename
                    updateschedule
                    url
                    urldownload
                    yeartype
                }
                data {
                    value
                }
                availableadjustments {
                    id
                    name
                    shortlabel
                }
                availablefilters {
                    attribute
                    description
                }
                ${children}
            }
        }`;

    const fetchOpt = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ query })
    };

    const req = await fetch(url, fetchOpt);
    this.metricTree = addDepth(stripNullChildren((await req.json()).data.metric));
    this.metrics = _.isEmpty(this.metrics) ? [this.metricTree.id] : this.metrics;
    callback(this.callbackFunctions, this.getModelState());
  }

  setStates(states) {
    this.states = states;
    callback(this.callbackFunctions, this.getModelState());
    return this.getModelState();
  }

  setHighlightState(state) {
    if (this.highlightState === state) {
      return this.getModelState();
    }
    this.highlightState = state;
    callback(this.callbackFunctions, this.getModelState());
    return this.getModelState();
  }
}

/*
// setChartType(integer) // 0 - 3 Need to check with Craig about small multiples
// setMetrics(array) // array of metric IDs
// setAdjustments(string) // adjustment string? or is it an id?
// setStates(null || array) // null for USA or array of fips code
// setCompareMode(string) // string 'metrics' or 'states'
// registerCallback(func) // will call all registered callbacks when model changes with object in this shape
// {
//     chartOptions, // options that could be directly passed to chart
//         chartType, // int
//         metrics, // array
//         adjustments, // string
//         states, // array or null
//         compareMode, // string
//         metricTree, // object,
//         availableAdjustments, // array
//         availableChartTypes, // array
//         hasStates // boolean
// }
*/
